import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertThresholds, CreateProjectForm } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { mapProjectToFormValues } from "../api/mappers.js";
import { useApi } from "../hooks/useApi.js";
import { AsyncBoundary, InlineMessage, errorText } from "../ui/Feedback.jsx";

/**
 * One component, two routes: /projects/new (create) and
 * /projects/:projectId/edit (edit via `initialValues`).
 *
 * CreateProjectForm seeds its useState from `initialValues` only on mount, so
 * the form must not render until the project has loaded — hence the
 * AsyncBoundary and the `key` on the form.
 */
export default function ProjectFormScreen() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isEdit = Boolean(projectId);
  const [error, setError] = useState(null); // ApiError | null
  const [busy, setBusy] = useState(false);

  const query = useApi(
    () => (isEdit ? api.projects.get(projectId) : Promise.resolve(null)),
    [api, projectId, isEdit],
    { skip: !isEdit },
  );

  async function handleSubmit(values) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (isEdit) {
        await api.projects.update(projectId, values);
        navigate(`/projects/${projectId}`);
      } else {
        const created = await api.projects.create(values);
        navigate(created?.id ? `/projects/${created.id}` : "/");
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error ? (
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <InlineMessage tone={error.code === "quota_exceeded" ? "warning" : "danger"}>
            {errorText(error, "Could not save the project.")}
            {error.code === "quota_exceeded" ? (
              <>
                {" "}
                <button
                  type="button"
                  onClick={() => navigate("/upgrade")}
                  style={{ color: "var(--text-accent)", background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}
                >
                  See your plan options
                </button>
                .
              </>
            ) : null}
          </InlineMessage>
        </div>
      ) : null}

      <AsyncBoundary query={query}>
        {(project) => (
          <>
            <CreateProjectForm
              key={projectId ?? "new"}
              initialValues={isEdit ? mapProjectToFormValues(project) : undefined}
              onSubmit={handleSubmit}
            />
            {/* The same section the settings screen shows, because this screen is
                also where a customer edits a project: the two must not disagree
                about which settings exist. Not on the create route — there is no
                project to attach a threshold to yet. */}
            {isEdit ? (
              <div style={{ maxWidth: 480, margin: "28px auto 0" }}>
                <AlertThresholds projectId={projectId} />
              </div>
            ) : null}
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
